import { useDispatch } from 'react-redux';
import { useState, ChangeEvent } from 'react';

import useConfig from 'hooks/useConfig';
// project import
import MainCard from 'components/MainCard';
// types
import { RootStateProps } from 'types/root';
// material-ui
import { useTheme } from '@mui/material/styles';
import { openDrawer } from 'store/reducers/menu';
import rtlLayout from 'assets/images/customization/rtl.svg';
import miniMenu from 'assets/images/customization/mini-menu.svg';
// assets
import defaultLayout from 'assets/images/customization/default.svg';
import { CardMedia, FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography } from '@mui/material';

import { useSelector } from '../../../../../store';
import { useChangeMode } from '../../../../../hooks/useChangeMode';

// ==============================|| CUSTOMIZATION - LAYOUT ||============================== //

const ThemeLayout = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { miniDrawer, themeDirection, onChangeDirection, onChangeMiniDrawer } = useConfig();
  const { drawerOpen } = useSelector((state: RootStateProps) => state.menu);

  let initialTheme = 'default';
  if (miniDrawer) initialTheme = 'mini';
  if (themeDirection === 'rtl') initialTheme = 'rtl';
  const user = useSelector((state) => state.user);
  const { i18n } = useConfig();
  const { changeUserMode } = useChangeMode();
  const [value, setValue] = useState<string | null>(initialTheme);
  const layout = user.personal_account_settings?.theme;

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);

    if (newValue === 'default') {
      onChangeDirection('ltr');

      if (layout?.theme_layout) {
        changeUserMode(i18n, { ...layout, theme_layout: newValue });
      }

      if (!drawerOpen) {
        dispatch(openDrawer({ drawerOpen: true }));
      }
    }

    if (newValue === 'mini') {
      onChangeMiniDrawer(true);

      if (layout?.theme_layout) {
        changeUserMode(i18n, { ...layout, theme_layout: newValue });
      }

      if (drawerOpen) {
        dispatch(openDrawer({ drawerOpen: false }));
      }
    }

    if (newValue === 'rtl') {
      if (layout?.theme_layout) {
        changeUserMode(i18n, { ...layout, theme_layout: newValue });
      }

      onChangeDirection('rtl');
    }
  };

  return (
    <RadioGroup row aria-label="payment-card" name="payment-card" value={value} onChange={handleRadioChange}>
      <Grid container spacing={1.75} sx={{ ml: 0 }}>
        <Grid item>
          <FormControlLabel
            value="default"
            control={<Radio value="default" sx={{ display: 'none' }} />}
            sx={{ display: 'flex', '& .MuiFormControlLabel-label': { flex: 1 } }}
            label={
              <MainCard
                content={false}
                sx={{ bgcolor: value === 'default' ? 'primary.lighter' : 'secondary.lighter', p: 1 }}
                border={false}
                {...(value === 'default' && { boxShadow: true, shadow: theme.customShadows.primary })}
              >
                <Stack spacing={1.25} alignItems="center">
                  <CardMedia component="img" src={defaultLayout} alt="Vertical" sx={{ borderRadius: 1, width: 64, height: 64 }} />
                  <Typography variant="caption">Default</Typography>
                </Stack>
              </MainCard>
            }
          />
        </Grid>
        <Grid item>
          <FormControlLabel
            value="mini"
            control={<Radio value="mini" sx={{ display: 'none' }} />}
            sx={{ display: 'flex', '& .MuiFormControlLabel-label': { flex: 1 } }}
            label={
              <MainCard
                content={false}
                sx={{ bgcolor: value === 'mini' ? 'primary.lighter' : 'secondary.lighter', p: 1 }}
                border={false}
                {...(value === 'mini' && { boxShadow: true, shadow: theme.customShadows.primary })}
              >
                <Stack spacing={1.25} alignItems="center">
                  <CardMedia component="img" src={miniMenu} alt="Vertical" sx={{ borderRadius: 1, width: 64, height: 64 }} />
                  <Typography variant="caption">Mini Drawer</Typography>
                </Stack>
              </MainCard>
            }
          />
        </Grid>
        <Grid item>
          <FormControlLabel
            value="rtl"
            control={<Radio value="rtl" sx={{ display: 'none' }} />}
            sx={{ display: 'flex', '& .MuiFormControlLabel-label': { flex: 1 } }}
            label={
              <MainCard
                content={false}
                sx={{ bgcolor: value === 'rtl' ? 'primary.lighter' : 'secondary.lighter', p: 1 }}
                border={false}
                {...(value === 'rtl' && { boxShadow: true, shadow: theme.customShadows.primary })}
              >
                <Stack spacing={1.25} alignItems="center">
                  <CardMedia component="img" src={rtlLayout} alt="Vertical" sx={{ borderRadius: 1, width: 64, height: 64 }} />
                  <Typography variant="caption">RTL</Typography>
                </Stack>
              </MainCard>
            }
          />
        </Grid>
      </Grid>
    </RadioGroup>
  );
};

export default ThemeLayout;
